import {
  powurOsAdderRegionAdderSchema,
  powurOSEquipmentAssociationSchema,
  powurOSInverterDataSchema,
  powurOsManufacturerSchema,
  powurOsModuleDataSchema,
  powurOsComputerDataSchema,
  powurOsEnergyBackupDataSchema,
  powurOsProposalPriceSchema,
  powurOsWarrantySchema,
  ROOF_TYPES,
  basicAdderAssociationSchema,
} from "@logic/lib/powur-os"
import type { RoofType } from "@logic/lib/powur-os"
import { pvWattsV8InputsSchema } from "@pacts/lib/pv-watts/v8/models"
import { z } from "zod"

import type { DesignWithEquipment } from "./types"
import type {
  DesignRequest as PrismaDesignRequest,
  DesignNote as PrismaDesignNote,
  DesignUser as PrismaDesignUser,
  Enterprise as PrismaEnterprise,
} from "../../prisma/generated"
import { isPolygon2d, isRectangle2d } from "../2d"
import { dateSchema } from "../generic"
import type { Edge2d, Face2d } from "../mesh"
import { isNode2d, isFace2d, isEdge2d } from "../mesh"
import { savingsAnalysisSchema } from "../savings"
import type { Index1ToMany } from "../utils/join"

export * from "./webRadiansToAzimuth"
export * from "./placePanels"
export * from "./placePotentialPanels"
export * from "./projector"
export * from "./types"
export * from "./get-design-cost-items"
export * from "./centerPanelWithinPadding"
export * from "./design-equipment"
export * from "./inverters"

export const imagerySchema = z.object({
  id: z.string(),
  source: z.string(),
  filename: z.string().nullable(),
  height: z.number().nullable(),
  width: z.number().nullable(),
  positionedY: z.number().nullable(),
  positionedX: z.number().nullable(),
  positionedZoom: z.number().nullable(),
  zoomLevel: z.number().nullable(),
  imageDate: z.string().nullable(),
  // i'm writing these fields as string or date
  // because the client believes it's a string
  // and the server believes it's a date
  createdAt: z.date().nullable().optional().or(z.string().optional().nullable()),
  updatedAt: z.date().nullable().optional().or(z.string().optional().nullable()),
  siteId: z.string().optional().nullable(),
})

export type Imagery = z.infer<typeof imagerySchema>

export const ROOF_EDGE_TYPES = [
  `eave`,
  `hip`,
  `rake`,
  `ridge`,
  `valley`,
] as const
export type RoofEdgeType = (typeof ROOF_EDGE_TYPES)[number]

export function isRoofEdgeType(x: unknown): x is RoofEdgeType {
  return ROOF_EDGE_TYPES.includes(x as RoofEdgeType)
}

export const MOUNT_LOCATIONS = [`ground`, `roof`] as const
export type MountLocation = (typeof MOUNT_LOCATIONS)[number]

export const DESIGN_SOURCES = [`vision`, `aurora`] as const

export function isMountLocation(x: unknown): x is MountLocation {
  return MOUNT_LOCATIONS.includes(x as MountLocation)
}

export type Setbacks = Record<RoofEdgeType, number>

export function isSetbacks(x: unknown): x is Setbacks {
  return (
    typeof x === `object` &&
    Object.entries(x as Setbacks).every(
      ([edge, number]) => isRoofEdgeType(edge) && typeof number === `number`,
    )
  )
}

export interface RoofEdge extends Edge2d {
  edgeType: RoofEdgeType
}

export function isRoofEdge(x: unknown): x is RoofEdge {
  return (
    isEdge2d(x) &&
    (typeof (x as RoofEdge).edgeType === `string` ||
      (x as RoofEdge).edgeType === null)
    // && typeof (x as RoofEdge).setback === `number`
  )
}

export const profileSchema = z.object({
  id: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  type: z.enum([`usage_pre`, `usage_post`, `solar_post`, `storage_post`]),
  profileId: z.string(),
  providerProfileId: z.string(),
  data: z.unknown().optional(),
})

export const panelSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  r: z.number(),
  isActive: z.boolean().nullable().default(null),
  xSnap: z.number().nullable().optional(),
  ySnap: z.number().nullable().optional(),
  wOriginal: z.number().optional(),
  hOriginal: z.number().optional(),
  orientation: z.number().nullable().optional(),
  module: z.string().nullable().default(null),
  moduleOsId: z.number().nullable().default(null),
})

export type Panel = z.infer<typeof panelSchema>

export function isPanel(x: unknown): x is Panel {
  return isRectangle2d(x) && typeof (x as Panel)?.isActive === `boolean`
}

export type InstallAreasToPanels = Index1ToMany<string, string>
export function isInstallAreasToPanelsRecord(
  x: unknown,
): x is ReturnType<InstallAreasToPanels[`toJson`]> {
  return (
    typeof x === `object` &&
    Object.values(x as InstallAreasToPanels).every(
      ([installAreaId, panelIds]) =>
        typeof installAreaId === `string` &&
        Array.isArray(panelIds) &&
        panelIds.every((panelId) => typeof panelId === `string`),
    )
  )
}

export const GROUND_MATERIALS = [`concrete`, `grass`] as const
export type GroundMaterial = (typeof GROUND_MATERIALS)[number]
export function isGroundMaterial(x: unknown): x is GroundMaterial {
  return typeof x === `string` && GROUND_MATERIALS.includes(x as GroundMaterial)
}

export const ROOF_MATERIALS = [
  `concrete`,
  `glass`,
  `metal`,
  `shingles`,
  `thatch`,
  `wood`,
] as const
export type RoofMaterial = (typeof ROOF_MATERIALS)[number]
export function isRoofMaterial(x: unknown): x is RoofMaterial {
  return ROOF_MATERIALS.includes(x as RoofMaterial)
}

export type Substrate = GroundMaterial | RoofMaterial
export function isSubstrate(x: unknown): x is Substrate {
  return isGroundMaterial(x) || isRoofMaterial(x)
}

export interface InstallArea extends Face2d {
  material: Substrate
  mountLocation: MountLocation
  azimuth: number | null // rotation clockwise from north in degrees
  azimuthLocked: boolean
  slope: number
  manualShading: number | null
  sunroofShading: number | null
  preferSunroof: boolean
  // panelIds: string[]
}
export function isInstallArea(x: unknown): x is InstallArea {
  return (
    isPolygon2d(x) &&
    isSubstrate((x as InstallArea).material) &&
    isMountLocation((x as InstallArea).mountLocation) &&
    typeof (x as InstallArea).azimuth === `number` &&
    typeof (x as InstallArea).slope === `number`
  )
}

export function isRoofType(x: unknown): x is RoofType {
  return ROOF_TYPES.includes(x as RoofType)
}

export const PHASES = [
  `Image Selection`,
  `Outline`,
  `Edge Designation`,
  `Roof Attributes`,
  `Panel Placement`,
  `Panel Activation`,
] as const
export type Phase = (typeof PHASES)[number]

export const TOOLS = [
  `Select`,
  `Draw`,
  `Pan`,
  `Erase`,
  `PanelCreatorLandscape`,
  `PanelCreatorPortrait`,
  `PanelActivator`,
  `PanelEraser`,
  `GroundMount`,
  `Trees`,
  `EdgeDesignator`,
  `Move`,
  `Ruler`,
] as const
export type Tool = (typeof TOOLS)[number]

export function isDesign(x: unknown): x is Design {
  return (
    typeof x === `object` &&
    isSetbacks((x as Design).setbacks) &&
    Object.entries((x as Design).nodes).every(
      ([id, node]) => typeof id === `string` && isNode2d(node),
    ) &&
    Object.entries((x as Design).edges).every(
      ([id, edge]) => typeof id === `string` && isRoofEdge(edge),
    ) &&
    isRoofType((x as Design).type) &&
    Object.entries((x as Design).installAreas).every(
      ([id, installArea]) =>
        typeof id === `string` && isInstallArea(installArea),
    ) &&
    Object.entries((x as Design).obstructions).every(
      ([id, obstruction]) => typeof id === `string` && isFace2d(obstruction),
    ) &&
    Object.entries((x as Design).shadingAreas).every(
      ([id, shadingArea]) => typeof id === `string` && isFace2d(shadingArea),
    ) &&
    Object.entries((x as Design).panels).every(
      ([id, panel]) => typeof id === `string` && isPanel(panel),
    ) &&
    isInstallAreasToPanelsRecord((x as Design).installAreasToPanels)
  )
}

export const setbacksSchema = z.object({
  eave: z.number(),
  hip: z.number(),
  rake: z.number(),
  ridge: z.number(),
  valley: z.number(),
})

export const nodeSchema = z.object({ x: z.number(), y: z.number() })

export const edgeSchema = z.object({
  node1id: z.string(),
  node2id: z.string(),
  edgeType: z.enum(ROOF_EDGE_TYPES),
  setback: z.number().nullable().optional(),
})

export const installAreaSchema = z.object({
  material: z.enum(ROOF_MATERIALS).or(z.enum(GROUND_MATERIALS)),
  mountLocation: z.enum(MOUNT_LOCATIONS),
  azimuth: z.number().nullable().default(null),
  azimuthLocked: z.boolean(),
  slope: z.number(),
  nodeIds: z.array(z.string()),
  manualShading: z.nullable(z.number().min(-5).max(100).default(3)),
  sunroofShading: z.nullable(z.number().min(-5).max(100).default(3)),
  preferSunroof: z.boolean().default(true),
})

export const installAreasSchema = z.record(z.string(), installAreaSchema)

export const face2dSchema = z.object({
  nodeIds: z.array(z.string()),
})

export const safeEstimateSchema = z.object({
  id: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  acAnnual: z.number(),
  acHourly: z.array(z.number()),
  acMonthly: z.array(z.number()),
  designId: z.string().nullable(),
  errors: z.array(z.string()),
  inputs: pvWattsV8InputsSchema.nullable().default(null),
  installAreaId: z.string().nullable(),
  poisonId: z.string().optional().nullable().default(null),
  solarProfile: profileSchema.nullable(),
  solarProfileId: z.string().nullable(),
  sscInfo: z.object({}).nullable().optional(),
  stationInfo: z.object({}).nullable().optional(),
})

export type SafeEstimate = z.infer<typeof safeEstimateSchema>

export const poisonedEstimateSchema = z.object({
  id: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  reason: z.string(),
  data: z.object({}),
})

export const estimateResultSchema = z.union([
  safeEstimateSchema,
  poisonedEstimateSchema,
])

export type Estimate = z.infer<typeof safeEstimateSchema>

export const equipmentTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  powurOsId: z.number().int(),
})
export type EquipmentType = z.infer<typeof equipmentTypeSchema>

export const manufacturerSchema = z.object({
  id: z.string(),
  powurOsId: z.number(),
  name: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
})
export type Manufacturer = z.infer<typeof manufacturerSchema>

export const regionSchema = z.object({
  id: z.string(),
  powurOsId: z.number(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  name: z.string(),
  regionCode: z.string().nullable(),
  postalCodes: z.string().nullable().default(null),
  getPostCodes: z.array(z.string()),
  adderRegionAdders: z
    .array(powurOsAdderRegionAdderSchema)
    .nullable()
    .default(null),
  activatedAt: dateSchema.nullable().default(null),
})
export type Region = z.infer<typeof regionSchema>

const compatibleManufactureSchema = z
  .object({
    compatibleInverterManufacturerIds: z.array(z.number().int()),
  })
  .nullable()

const batteryDataSchema = z
  .object({
    compatibleInverterManufacturerIds: z.array(z.number().int()),
    hasBackupCapability: z.boolean().nullable().optional(),
    qty: z.number().int().nonnegative().nullable().optional(),
    leadBatteryQty: z.number().int().nonnegative().nullable().optional(),
    followerBatteryQty: z.number().int().nonnegative().nullable().optional(),
    usableBatteryCapacityKilowatts: z.number().nullable().optional(),
    roundTripEfficiency: z.number().nullable().optional(),
    ratedContinuousOutputPowurKilowatts: z.number().nullable().optional(),
  })
  .nullable()

export const unpricedEquipmentSchema = z.object({
  id: z.number().int(),
  deprecated_id: z.string().nullable().optional(),
  basicAdderAssociations: z.array(basicAdderAssociationSchema),
  batteryData: batteryDataSchema.optional(),
  catalogNumber: z.string().nullable().default(null),
  cecWeightedEfficiency: z.number().default(0),
  childEquipmentAssociations: z
    .array(powurOSEquipmentAssociationSchema.nullable().optional())
    .nullable()
    .default([]),
  compatibleInverterManufacturers: z.array(
    z.object({
      equipmentId: z.number().int().optional(),
      manufacturerId: z.number().int().optional(),
      manufacturer: powurOsManufacturerSchema.optional(),
    }),
  ),
  createdAt: dateSchema,
  // currentProposalPrice: z.number().optional(),
  description: z.string().nullable().default(null),
  energyBackupData: powurOsEnergyBackupDataSchema.nullable().default(null),
  equipmentTypeId: z.string(),
  equipmentType: equipmentTypeSchema.optional().nullable(),
  fallbackPrice: z.number().nullable().default(null),
  height: z.number().nullable().default(null),
  isKit: z.boolean().default(false),
  inverterData: powurOSInverterDataSchema.default(null),
  manufacturerId: z.string().nullable(),
  manufacturer: manufacturerSchema.optional().nullable(),
  moduleData: powurOsModuleDataSchema.nullable().default(null),
  computerData: powurOsComputerDataSchema.nullish(),
  name: z.string(),
  parentEquipmentAssociations: z.union([
    z.null(),
    z.undefined(),
    z.array(powurOSEquipmentAssociationSchema),
  ]),
  powurOsEnergyBackupDataId: z.number().nullable().default(null).optional(),
  powurOsEquipmentTypeId: z.number(),
  powurOsInverterDataId: z.number().nullable().default(null),
  powurOsModuleDataId: z.number().nullable().default(null),
  powurOsManufacturerId: z.number().nullable(),
  proposalPrices: z.array(powurOsProposalPriceSchema),
  updatedAt: dateSchema,
  warranty: powurOsWarrantySchema.nullable().default(null),
  wattage: z.number().nullable().default(null),
  width: z.number().nullable().default(null),
  isDefault: z.boolean().default(false),
})
export type UnpricedEquipment = z.infer<typeof unpricedEquipmentSchema>
export const equipmentSchema = unpricedEquipmentSchema.extend({
  currentProposalPrice: z.number(),
})
export type Equipment = z.infer<typeof equipmentSchema>

/**
 * Schema for NewEquipment returned by equipmentForProposal GraphQL query.
 * This is the flattened, simplified equipment shape from PR #3350.
 *
 * Note: Some fields (isDomesticContent, manufacturerNumber, type) are optional
 * because the AllNewEquipmentFields fragment doesn't request them.
 * Update the fragment if these fields are needed.
 */
export const newEquipmentSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  proposalName: z.string().nullable(),
  typeId: z.number().int(),
  currentProposalPrice: z.number(),
  description: z.string().nullable(),
  manufacturerId: z.number().int(),
  manufacturer: z.string(),
  catalogNumber: z.string().nullable(),
  isKit: z.boolean(),
  isBattery: z.boolean(),
  isDefault: z.boolean(),
  isDomesticContent: z.boolean().optional(),
  batteryData: batteryDataSchema,
  evChargerData: compatibleManufactureSchema,
  wattage: z.number().nullable(),
  height: z.number().nullable(),
  width: z.number().nullable(),
  manufacturerNumber: z.string().nullable().optional(),
  type: z.string().optional(),
})
export type NewEquipment = z.infer<typeof newEquipmentSchema>

export const baseDesignEquipmentSchema = z.object({
  id: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  equipmentId: z.number().int(),
  powurOsEquipmentTypeId: z.number().int(),
  designId: z.string(),
  quantity: z.number(),
  price: z.number(),
})
export const designEquipmentSchema = baseDesignEquipmentSchema.extend({
  equipment: equipmentSchema.optional().nullable(),
})
export type RelatedEquipment = z.infer<typeof designEquipmentSchema>
export const unpricedDesignEquipmentSchema = baseDesignEquipmentSchema.extend({
  equipment: unpricedEquipmentSchema.optional().nullable(),
})

export const installAreasToPanelsSchema = z.array(
  z.tuple([z.string(), z.array(z.string())]),
)

export type InstallAreasToPanelsSchemaType = z.infer<
  typeof installAreasToPanelsSchema
>

export const panelsSchema = z.record(z.unknown()).transform((input) => {
  const panels: Record<string, Panel> = {}
  for (const [key, value] of Object.entries(input)) {
    const result = panelSchema.safeParse(value)
    switch (result.success) {
      case true:
        panels[key] = result.data
        break
      case false:
        console.error(result.error)
        break
      default:
        console.error({ result })
        throw new Error(`Unexpected result`)
    }
  }

  return panels
})

export type PanelRecord = z.infer<typeof panelsSchema>

// [eyecue-codemap-group:9PaVBf7tv1S]
export const baseDesignSchema = z.object({
  id: z.string(),
  activatedAt: dateSchema.nullable().default(null),
  designEquipment: z.array(equipmentSchema).nullable().default(null),
  batteryCompatibility: z.boolean(),
  brochurePositionX: z.number().default(0),
  brochurePositionY: z.number().default(0),
  brochureZoom: z.number().default(1.1),
  completedAt: dateSchema.nullable().default(null),
  annualProduction: z.number().nullable().default(null),
  annualOffset: z.number().nullable().default(null),
  systemSizeKw: z.number().nullable().default(null),
  createdAt: dateSchema,
  designRequestId: z.string().nullable().default(null),
  displayImageryProvider: z.string().default(`nearmap`),
  edges: z.record(z.string(), edgeSchema),
  energyBackupId: z.number().nullable().default(null),
  energyEfficiencyKitRebate: z.boolean(),
  energyEfficiencyKitRebateAmount: z.number(),
  optimalEstimate: estimateResultSchema.optional().nullable().default(null),
  estimates: estimateResultSchema.array().optional().nullable().default([]),
  evOutlet: z.boolean(),
  evCharger: z.boolean(),
  extendedWarrantyPowurOsId: z.number().nullable().default(null),
  extendedWarrantyName: z.string().nullable().default(null),
  extendedWarrantySpanishName: z.string().nullable().default(null),
  extendedWarrantyFamily: z.string().nullable().default(null),
  extendedWarrantyCost: z.number().nullable().default(null),
  homeownerRebate: z.boolean(),
  homeownerRebateAmount: z.number(),
  installAreas: z.record(z.string(), installAreaSchema),
  installAreasToPanels: installAreasToPanelsSchema,
  inverterId: z.number().nullable().default(null),
  inverterManufacturer: z.string().nullable().default(null),
  name: z.string().optional().nullable(),
  nodes: z.record(z.string(), nodeSchema),
  obstructions: z.record(z.string(), face2dSchema),
  panels: panelsSchema,
  parentId: z.string().nullable().default(null),
  phase: z.enum(PHASES).transform((phase) => {
    if (PHASES.includes(phase)) {
      return phase
    }

    return `Outline`
  }),
  proposalId: z.string(),
  profiles: z.array(profileSchema).optional().default([]),
  roofType: z.string().nullable().default(null),
  roofWorkRebate: z.boolean(),
  roofWorkRebateAmount: z.number(),
  savings: savingsAnalysisSchema.nullable().default(null),
  secondaryInverterId: z.number().nullable().default(null),
  setbacks: setbacksSchema,
  shadingAreas: z.record(z.string(), face2dSchema),
  source: z.enum(DESIGN_SOURCES),
  sunroofCheckedAt: dateSchema.nullable().default(null),
  tool: z.enum(TOOLS).transform((tool) => {
    if (TOOLS.includes(tool)) {
      return tool
    }

    return `Select`
  }),
  treeTrimmingRebate: z.boolean(),
  treeTrimmingRebateAmount: z.number(),
  type: z.string().nullable().default(null),
  updatedAt: dateSchema,
  openSolarImage: imagerySchema.optional().nullable(),
  poisonId: z.string().optional().nullable(),
  poison: z
    .object({
      reason: z.string(),
      data: z.unknown(),
    })
    .optional()
    .nullable(),
})
export type BaseDesign = z.infer<typeof baseDesignSchema>
export function designTransformer<
  D extends BaseDesignWithPricedEquipment | BaseDesignWithUnpricedEquipment,
>(design: D): D {
  const safePanelIds = Object.keys(design.panels)
  const safeInstallAreaIds = Object.keys(design.installAreas)
  design.installAreasToPanels = design.installAreasToPanels
    .filter(([installAreaId]) => safeInstallAreaIds.includes(installAreaId))
    .map(([installAreaId, panelIds]) => [
      installAreaId,
      panelIds.filter((id) => safePanelIds.includes(id)),
    ])

  return design
}
export const baseDesignSchemaWithPricedEquipment = baseDesignSchema.extend({
  energyBackup: equipmentSchema.nullable().default(null),
  inverter: equipmentSchema.nullable().default(null),
  secondaryInverter: equipmentSchema.optional().nullable().default(null),
  designEquipment: z.array(designEquipmentSchema).optional().default([]),
})
export type BaseDesignWithPricedEquipment = z.infer<
  typeof baseDesignSchemaWithPricedEquipment
>

export const designSchema =
  baseDesignSchemaWithPricedEquipment.transform(designTransformer)
export type Design = z.infer<typeof designSchema>

export const baseDesignWithUnpricedEquipmentSchema = baseDesignSchema.extend({
  energyBackup: unpricedEquipmentSchema.nullable().default(null),
  inverter: unpricedEquipmentSchema.nullable().default(null),
  designEquipment: z.array(unpricedDesignEquipmentSchema).optional().default([]),
  secondaryInverter: unpricedEquipmentSchema.nullable().default(null),
})
export type BaseDesignWithUnpricedEquipment = z.infer<
  typeof baseDesignWithUnpricedEquipmentSchema
>

export const designWithUnpricedEquipmentSchema =
  baseDesignWithUnpricedEquipmentSchema.transform(designTransformer)

export type DesignWithUnpricedEquipment = z.infer<
  typeof designWithUnpricedEquipmentSchema
>

// [end-eyecue-codemap-group:9PaVBf7tv1S:093a7be6cad2ca742f6048226c89485773a783e6]

export type DocShared = {
  id: string
  createdAt: Date | string
  location: string | null
  docType: string
  content: string
  updatedAt: Date | string | null
}

export const rebateKeys: [keyof Design, keyof Design][] = [
  [`energyEfficiencyKitRebate`, `energyEfficiencyKitRebateAmount`],
  [`treeTrimmingRebate`, `treeTrimmingRebateAmount`],
  [`homeownerRebate`, `homeownerRebateAmount`],
  [`roofWorkRebate`, `roofWorkRebateAmount`],
]

export type PrismaDesignRequestWithRelations = PrismaDesignRequest & {
  design: DesignWithEquipment | null
  designUser: PrismaDesignUser | null
  enterprise: PrismaEnterprise | null
  designNotes: PrismaDesignNote | null
}
